import React, { useState, useEffect } from 'react';
import { FiSend, FiMessageCircle } from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { Comment } from '../../types';
import { socketService } from '../../services/socketService';
import { showToast } from '../../utils/toast';
import CommentItem from './CommentItem';

interface CommentSectionProps {
  signalId: string;
  signalAuthorId: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ signalId, signalAuthorId }) => {
  const { user } = useAuthStore();
  const [commentText, setCommentText] = useState('');
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [showComments, setShowComments] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);

  const { data: commentsData } = useApi.useComments(signalId);
  const createCommentMutation = useApi.useCreateComment({
    onSuccess: (data) => {
      showToast('success', replyToCommentId ? 'Reply posted successfully!' : 'Comment posted successfully!');
      setCommentText('');
      setReplyToCommentId(null);
      // Don't call refetch - mutation already updates cache optimistically
    },
    onError: (error: any) => {
      showToast('error', error.response?.data?.error || 'Failed to post comment');
    },
  });

  // Listen for real-time comment updates via WebSocket (only for updates from other sessions)
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    // Socket service already updates cache, so we don't need to refetch
    // These listeners are only for cross-session updates
    const handleNewComment = (data: { signalId: string; comment: Comment }) => {
      if (data.signalId === signalId) {
        // Cache is already updated by socketService, no need to refetch
        console.log('💬 New comment received from another session');
      }
    };

    const handleCommentLike = (data: { signalId: string; commentId: string }) => {
      if (data.signalId === signalId) {
        // Cache is already updated by socketService, no need to refetch
        console.log('❤️ Comment like received from another session');
      }
    };

    const handleCommentDeleted = (data: { signalId: string; commentId: string }) => {
      if (data.signalId === signalId) {
        // Cache is already updated by socketService, no need to refetch
        console.log('🗑️ Comment deleted from another session');
      }
    };

    socket.on('comment:new', handleNewComment);
    socket.on('comment:like', handleCommentLike);
    socket.on('comment:deleted', handleCommentDeleted);

    return () => {
      socket.off('comment:new', handleNewComment);
      socket.off('comment:like', handleCommentLike);
      socket.off('comment:deleted', handleCommentDeleted);
    };
  }, [signalId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || createCommentMutation.isPending) return;

    createCommentMutation.mutate({
      signalId,
      text: commentText.trim(),
      parentCommentId: replyToCommentId || undefined,
    });
  };

  // Extended Comment type with replies
  interface CommentWithReplies extends Comment {
    replies?: Comment[];
  }

  // Organize comments into a tree structure
  const organizeComments = (comments: Comment[]): CommentWithReplies[] => {
    const commentMap = new Map<string, CommentWithReplies>();
    const rootComments: CommentWithReplies[] = [];

    // First pass: create map of all comments
    comments.forEach((comment) => {
      commentMap.set(comment._id, { ...comment, replies: [] });
    });

    // Second pass: build tree
    comments.forEach((comment) => {
      const commentWithReplies = commentMap.get(comment._id)!;
      if (comment.parentComment) {
        const parentId = typeof comment.parentComment === 'string' 
          ? comment.parentComment 
          : comment.parentComment._id;
        const parent = commentMap.get(parentId);
        if (parent) {
          if (!parent.replies) parent.replies = [];
          parent.replies.push(commentWithReplies);
        }
      } else {
        rootComments.push(commentWithReplies);
      }
    });

    return rootComments;
  };

  const comments = commentsData?.comments || [];
  const organizedComments = organizeComments(comments);
  const totalComments = comments.length;
  
  // Show only first comment initially, then all when expanded
  const displayedComments = showAllComments ? organizedComments : organizedComments.slice(0, 1);
  const hasMoreComments = organizedComments.length > 1;

  if (!user) return null;

  return (
    <div className="mt-4 border-t border-border pt-4">
      <button
        onClick={() => setShowComments(!showComments)}
        className="flex items-center gap-2 text-muted hover:text-foreground transition-colors mb-3"
      >
        <FiMessageCircle />
        <span className="text-sm font-medium">
          {showComments ? 'Hide' : 'Show'} Comments ({totalComments})
        </span>
      </button>

      {showComments && (
        <>
          {/* Comment Form */}
          <form onSubmit={handleSubmit} className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={replyToCommentId ? 'Write a reply...' : 'Write a comment...'}
                className="flex-1 min-w-0 px-3 py-2 bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={1000}
              />
              <button
                type="submit"
                disabled={!commentText.trim() || createCommentMutation.isPending}
                className="flex-shrink-0 px-3 sm:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FiSend />
                <span>Post</span>
              </button>
            </div>
            {replyToCommentId && (
              <button
                type="button"
                onClick={() => setReplyToCommentId(null)}
                className="mt-2 text-xs text-muted hover:text-foreground"
              >
                Cancel reply
              </button>
            )}
          </form>

          {/* Comments List */}
          <div className="space-y-0">
            {organizedComments.length === 0 ? (
              <p className="text-muted text-sm text-center py-4">
                No comments yet. Be the first to comment!
              </p>
            ) : (
              <>
                {displayedComments.map((comment) => (
                  <div key={comment._id}>
                    <CommentItem
                      comment={comment}
                      signalAuthorId={signalAuthorId}
                      onReply={setReplyToCommentId}
                      level={0}
                    />
                    {/* Render replies */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div className="ml-2 sm:ml-4 border-l-2 border-border pl-2 sm:pl-4">
                        {comment.replies.map((reply) => (
                          <CommentItem
                            key={reply._id}
                            comment={reply}
                            signalAuthorId={signalAuthorId}
                            onReply={setReplyToCommentId}
                            level={1}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Show "See more comments" button if there are more comments */}
                {hasMoreComments && !showAllComments && (
                  <button
                    onClick={() => setShowAllComments(true)}
                    className="w-full mt-4 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    See more comments ({organizedComments.length - 1} more)
                  </button>
                )}
                
                {/* Show "Show less" button if all comments are shown */}
                {hasMoreComments && showAllComments && (
                  <button
                    onClick={() => setShowAllComments(false)}
                    className="w-full mt-4 py-2 text-sm font-medium text-muted hover:text-foreground transition-colors"
                  >
                    Show less
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CommentSection;

